import { MediaFragment } from 'generated/graphql';

export const MAX_SWAP_PROPOSALS = 5;

export const TO_FAILURE_AMOUNT = 619;

// One dosage set of a type-2 exercise: `[amount, unit]`.
export type SwapSet = {
  reps: Array<number | string>;
};

export type SwapProposal = {
  name: string;
  description?: string | null;
  media?: MediaFragment | null;
  sets: SwapSet[];
};

export type ExerciseSwapConfig = {
  swapsEnabled: boolean;
  proposals: SwapProposal[];
};

// UI-local exercise identity (search candidate filtering, proposal
// tracking). Slot matching and write dedupe live in creator-api, which
// mirrors the engine's normalization - this stays consistent with both.
export const exerciseNameKey = (name: string): string =>
  name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

export const defaultSwapConfig = (): ExerciseSwapConfig => ({
  swapsEnabled: true,
  proposals: [],
});

export const proposalFromCatalog = (
  catalogExercise: {
    name: string;
    description?: string | null;
    media?: MediaFragment | null;
  },
  originalSets: SwapSet[] | undefined,
): SwapProposal => ({
  name: catalogExercise.name,
  description: catalogExercise.description,
  media: catalogExercise.media,
  sets: (originalSets ?? []).map((set) => ({ reps: [...set.reps] })),
});

export const isValidSet = (set: SwapSet): boolean => {
  const [amount, unit] = set.reps;
  const amountText = `${amount ?? ''}`.trim();
  return (
    amountText !== '' &&
    !Number.isNaN(Number(amountText)) &&
    typeof unit === 'string' &&
    unit.length > 0
  );
};

const setLabel = (set: SwapSet): string => {
  const [amount, unit] = set.reps;
  return Number(amount) === TO_FAILURE_AMOUNT
    ? 'To failure'
    : `${amount} ${unit}`;
};

export const doseLabel = (sets: SwapSet[]): string => {
  if (sets.length === 0) return 'No sets';
  const labels = sets.map(setLabel);
  return sets.length > 1 && labels.every((label) => label === labels[0])
    ? `${sets.length} x ${labels[0]}`
    : labels.join(', ');
};
